import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * Drop-in replacement for <input type="date">.
 *
 * Props (compatible with the native input where it matters):
 *   value     — string "YYYY-MM-DD" (or "")
 *   onChange  — (isoDateString) => void   // empty string when cleared
 *   minDate   — optional Date; defaults to today (blocks past dates)
 *   placeholder, className, disabled, dataTestId
 */
function toISO(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fromISO(iso) {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function formatDisplay(date) {
  if (!date) return "";
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function DatePicker({
  value,
  onChange,
  minDate,
  placeholder = "Pick a date",
  className = "",
  disabled = false,
  dataTestId,
  iconClassName = "",
}) {
  const [open, setOpen] = React.useState(false);
  const selected = fromISO(value);
  // Default min: today (no past bookings)
  const min = minDate || (() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={dataTestId}
          className={`relative w-full text-left input-field with-icon ${className} ${
            !value ? "text-text-secondary" : "text-text-primary"
          }`}
        >
          <CalendarIcon
            className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary pointer-events-none ${iconClassName}`}
          />
          <span className="block truncate">
            {selected ? formatDisplay(selected) : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[60] bg-white border border-border shadow-xl rounded-xl" align="start" sideOffset={6}>
        <DayPicker
          mode="single"
          selected={selected}
          onSelect={(d) => {
            if (!d) return;
            onChange(toISO(d));
            setOpen(false);
          }}
          disabled={{ before: min }}
          showOutsideDays
          className="rdp-mmc p-3"
          styles={{
            day_selected: { backgroundColor: "#1d4f3a", color: "#fff" },
          }}
          modifiersStyles={{
            selected: { backgroundColor: "#1d4f3a", color: "#fff" },
            today: { fontWeight: 700, color: "#1d4f3a" },
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
